/**
 * Lines, boxes, polylines and linefills from a Pine script, drawn on a canvas
 * over the price pane.
 *
 * Why a canvas rather than DOM nodes: a script may legitimately create
 * hundreds of objects (the engine caps them at the declaration's
 * max_lines_count), and hundreds of absolutely-positioned divs re-laid-out on
 * every pan is the difference between a chart that tracks the cursor and one
 * that stutters.
 *
 * Coordinates come from the chart, not from arithmetic of our own.
 * `logicalToCoordinate` maps a bar index — which is what Pine's
 * `xloc.bar_index` means — and resolves indices *past the last bar* too,
 * which is what an extended line or a box projected into the future needs.
 * `timeToCoordinate` cannot do that: it only knows timestamps already on the
 * scale, so using it would silently drop every object anchored ahead of the
 * data.
 */
import type { IChartApi, ISeriesApi, Time } from "lightweight-charts";
import type { PineBox, PineLine, PineLinefill, PinePolyline } from "@tt/shared";
import { useEffect, useRef } from "react";

export interface PineDrawingsProps {
  chart: IChartApi | null;
  series: ISeriesApi<"Candlestick"> | null;
  /** Bar open times, ascending — used to map a Pine `xloc.bar_time` anchor. */
  barTimes: number[];
  lines: PineLine[];
  boxes: PineBox[];
  polylines: PinePolyline[];
  linefills: PineLinefill[];
}

const DASH: Record<string, number[]> = {
  solid: [],
  dotted: [2, 3],
  dashed: [6, 4],
  arrow_left: [],
  arrow_right: [],
  arrow_both: [],
};

export function PineDrawings({
  chart,
  series,
  barTimes,
  lines,
  boxes,
  polylines,
  linefills,
}: PineDrawingsProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // The latest props, read by the draw loop. Keeping them in a ref means a
  // new script result repaints without tearing down the chart subscriptions.
  const data = useRef({ lines, boxes, polylines, linefills, barTimes });
  data.current = { lines, boxes, polylines, linefills, barTimes };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !chart || !series) return;

    let frame = 0;
    const draw = () => {
      frame = 0;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const host = canvas.parentElement;
      const w = host?.clientWidth ?? 0;
      const h = host?.clientHeight ?? 0;
      const dpr = window.devicePixelRatio || 1;
      if (
        canvas.width !== Math.round(w * dpr) ||
        canvas.height !== Math.round(h * dpr)
      ) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      const scale = chart.timeScale();
      const {
        lines: ls,
        boxes: bs,
        polylines: ps,
        linefills: fs,
        barTimes: bt,
      } = data.current;

      /** Pine x to a pixel. A bar index goes through the logical scale; a
       *  `bar_time` anchor is a timestamp in milliseconds. */
      const toX = (v: number | null, xloc: string): number | null => {
        if (v === null || !Number.isFinite(v)) return null;
        if (xloc === "bar_time") {
          const seconds = Math.floor(v / 1000);
          // Snap to the bar containing it: timeToCoordinate resolves only
          // timestamps that sit on the scale.
          let lo = 0;
          let hi = bt.length - 1;
          if (hi < 0 || seconds < bt[0]!) return null;
          if (seconds >= bt[hi]!) {
            const c = scale.timeToCoordinate(bt[hi]! as Time);
            return c === null ? null : (c as number);
          }
          while (lo < hi) {
            const mid = (lo + hi + 1) >> 1;
            if (bt[mid]! <= seconds) lo = mid;
            else hi = mid - 1;
          }
          const c = scale.timeToCoordinate(bt[lo]! as Time);
          return c === null ? null : (c as number);
        }
        const c = scale.logicalToCoordinate(v as never);
        return c === null || c === undefined ? null : (c as number);
      };

      const toY = (v: number | null): number | null => {
        if (v === null || !Number.isFinite(v)) return null;
        const c = series.priceToCoordinate(v);
        return c === null || c === undefined ? null : (c as number);
      };

      const stroke = (color: string | null, width: number, style: string) => {
        ctx.strokeStyle = color ?? "#2962ff";
        ctx.lineWidth = Math.max(1, width || 1);
        ctx.setLineDash(DASH[style] ?? []);
      };

      /** A line's endpoints in pixels, with `extend` applied to the edges. */
      const span = (ln: PineLine) => {
        const x1 = toX(ln.x1, ln.xloc);
        const x2 = toX(ln.x2, ln.xloc);
        const y1 = toY(ln.y1);
        const y2 = toY(ln.y2);
        if (x1 === null || x2 === null || y1 === null || y2 === null)
          return null;
        let [ax, ay, bx, by] = [x1, y1, x2, y2];
        const dx = bx - ax;
        const dy = by - ay;
        if (dx !== 0 || dy !== 0) {
          // Extend by solving for the boundary, so a line drawn right-to-left
          // extends the way it was asked to rather than always rightward.
          const at = (t: number) => [ax + dx * t, ay + dy * t] as const;
          if (ln.extend === "left" || ln.extend === "both") {
            const t = dx !== 0 ? -ax / dx : -ay / dy;
            if (Number.isFinite(t)) [ax, ay] = at(Math.min(t, 0));
          }
          if (ln.extend === "right" || ln.extend === "both") {
            const t = dx !== 0 ? (w - ax) / dx : (h - ay) / dy;
            if (Number.isFinite(t)) [bx, by] = at(Math.max(t, 1));
          }
        }
        return { ax, ay, bx, by };
      };

      // Fills first, so a linefill sits under the lines that bound it.
      const byId = new Map(ls.map((l) => [l.id, l]));
      for (const f of fs) {
        const a = f.line1 ? byId.get(f.line1) : undefined;
        const b = f.line2 ? byId.get(f.line2) : undefined;
        if (!a || !b) continue;
        const sa = span(a);
        const sb = span(b);
        if (!sa || !sb) continue;
        ctx.beginPath();
        ctx.moveTo(sa.ax, sa.ay);
        ctx.lineTo(sa.bx, sa.by);
        ctx.lineTo(sb.bx, sb.by);
        ctx.lineTo(sb.ax, sb.ay);
        ctx.closePath();
        ctx.fillStyle = f.color ?? "#2962ff20";
        ctx.fill();
      }

      for (const bx of bs) {
        const l = toX(bx.left, bx.xloc);
        const r = toX(bx.right, bx.xloc);
        const t = toY(bx.top);
        const b = toY(bx.bottom);
        if (l === null || r === null || t === null || b === null) continue;
        const x = Math.min(l, r);
        const y = Math.min(t, b);
        const bw = Math.abs(r - l);
        const bh = Math.abs(b - t);
        if (bx.bgcolor) {
          ctx.fillStyle = bx.bgcolor;
          ctx.fillRect(x, y, bw, bh);
        }
        if (bx.border_width > 0) {
          stroke(bx.border_color, bx.border_width, bx.border_style);
          ctx.strokeRect(x, y, bw, bh);
        }
        if (bx.text) {
          ctx.fillStyle = bx.text_color ?? "#ffffff";
          ctx.font = "12px ui-sans-serif, system-ui, sans-serif";
          ctx.textAlign =
            bx.text_halign === "left"
              ? "left"
              : bx.text_halign === "right"
                ? "right"
                : "center";
          ctx.textBaseline =
            bx.text_valign === "top"
              ? "top"
              : bx.text_valign === "bottom"
                ? "bottom"
                : "middle";
          const tx =
            bx.text_halign === "left"
              ? x + 4
              : bx.text_halign === "right"
                ? x + bw - 4
                : x + bw / 2;
          const ty =
            bx.text_valign === "top"
              ? y + 4
              : bx.text_valign === "bottom"
                ? y + bh - 4
                : y + bh / 2;
          ctx.fillText(bx.text, tx, ty);
        }
      }

      for (const pl of ps) {
        const pts: [number, number][] = [];
        for (const p of pl.points) {
          const x =
            pl.xloc === "bar_time"
              ? toX(p.time, "bar_time")
              : toX(p.index, "bar_index");
          const y = toY(p.price);
          if (x === null || y === null) continue;
          pts.push([x, y]);
        }
        if (pts.length < 2) continue;
        ctx.beginPath();
        ctx.moveTo(pts[0]![0], pts[0]![1]);
        for (let k = 1; k < pts.length; k++) ctx.lineTo(pts[k]![0], pts[k]![1]);
        if (pl.closed) ctx.closePath();
        if (pl.fill_color) {
          ctx.fillStyle = pl.fill_color;
          ctx.fill();
        }
        stroke(pl.line_color, pl.line_width, pl.line_style);
        ctx.stroke();
      }

      for (const ln of ls) {
        const s = span(ln);
        if (!s) continue;
        stroke(ln.color, ln.width, ln.style);
        ctx.beginPath();
        ctx.moveTo(s.ax, s.ay);
        ctx.lineTo(s.bx, s.by);
        ctx.stroke();
      }
      ctx.setLineDash([]);
    };

    // Coalesce every trigger into one paint per frame: panning fires the
    // range handler far more often than the display refreshes.
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(draw);
    };

    const scale = chart.timeScale();
    scale.subscribeVisibleLogicalRangeChange(schedule);
    const ro = new ResizeObserver(schedule);
    if (canvas.parentElement) ro.observe(canvas.parentElement);
    schedule();

    return () => {
      if (frame) cancelAnimationFrame(frame);
      scale.unsubscribeVisibleLogicalRangeChange(schedule);
      ro.disconnect();
    };
    // Redraw when the objects themselves change, as well as on pan and resize.
  }, [chart, series, lines, boxes, polylines, linefills]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        // Decoration: the chart underneath must keep every gesture.
        pointerEvents: "none",
        zIndex: 2,
      }}
    />
  );
}
